import React, { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Home, ChevronRight, ChevronDown, SlidersHorizontal, Search, MapPin, X, LayoutGrid, Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VenueCard from "../components/VenueCard";
import { CITIES } from "../mock/mockData";
import { districtsOf } from "../lib/districts";
import { useCategories } from "../context/CategoriesContext";
import { Seo, cityFromSlug, citySlug, breadcrumbJsonLd } from "../lib/seo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 24;

const LoadMore = ({ onClick, left, loading }) => (
  <div className="flex justify-center mt-10">
    <button onClick={onClick} disabled={loading} data-testid="listing-load-more" className="disabled:opacity-60 px-6 h-12 rounded-full border border-[#C15B6B] text-[#9E2A3B] text-sm font-medium hover:bg-[#FBEEF0] transition-colors">
      {loading ? "Yükleniyor..." : `Daha Fazla Göster (${left.toLocaleString("tr-TR")} kaldı)`}
    </button>
  </div>
);

const Searching = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="listing-loading">
    <Loader2 className="w-8 h-8 text-[#C15B6B] animate-spin" />
    <p className="font-display text-2xl text-[#2A2224] mt-4">Firmalar bulunuyor...</p>
    <p className="text-[#7C6E70] mt-2">Sizin için en uygun firmaları listeliyoruz, lütfen bekleyin.</p>
  </div>
);

const NearbyFallback = ({ city, districts, nearby }) => (
  <div data-testid="nearby-fallback">
    <div className="text-center py-10">
      <p className="font-display text-2xl text-[#2A2224]">Seçtiğiniz {districts.length ? "ilçede" : "şehirde"} henüz firma yok</p>
      <p className="text-[#7C6E70] mt-2">
        {nearby.length ? `${districts.join(", ")} yerine ${city} içindeki en yakın firmalara göz atın.` : `${city} içinde bu kategoride henüz kayıtlı firma yok.`}
      </p>
    </div>
    {nearby.length > 0 && (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="nearby-venues">
        {nearby.map((v, i) => <VenueCard key={v.slug || v.id} venue={v} index={i} />)}
      </div>
    )}
  </div>
);

const CategoryListing = () => {
  const routeParams = useParams();
  const slug = routeParams.slug || routeParams.categorySlug;
  const seoCity = routeParams.citySlug ? cityFromSlug(routeParams.citySlug) : null;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getCategory, categories } = useCategories();
  const ALL_MEKAN = useMemo(() => {
    const first = categories.find((c) => c.group === "mekan");
    return { slug: "dugun-mekanlari", name: "Düğün Mekanları", group: "mekan", image: first?.image };
  }, [categories]);
  const category = slug === "dugun-mekanlari" ? ALL_MEKAN : getCategory(slug);
  const groupCats = useMemo(
    () => [...categories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [categories]
  );
  const goCategory = (s) => {
    const qs = new URLSearchParams();
    if (cityFilter !== "hepsi") qs.set("sehir", cityFilter);
    if (cityFilter !== "hepsi" && districtFilters.length === 1) qs.set("ilce", districtFilters[0]);
    const q = qs.toString() ? `?${qs}` : "";
    if (seoCity && cityFilter === seoCity) { navigate(`/${s}/${routeParams.citySlug}${q.replace(/sehir=[^&]*&?/, "").replace(/\?$/, "")}`); return; }
    navigate(`/kategori/${s}${q}`);
  };
  const [venues, setVenues] = useState([]);
  const [total, setTotal] = useState(0);
  const [cityTotal, setCityTotal] = useState(0);
  const [districtCounts, setDistrictCounts] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const reqId = useRef(0);

  const [cityFilter, setCityFilter] = useState(seoCity || searchParams.get("sehir") || "hepsi");
  const [sort, setSort] = useState("populer");
  const [query, setQuery] = useState("");
  const [districtFilters, setDistrictFilters] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [ilceOpen, setIlceOpen] = useState(false);

  useEffect(() => {
    setCityFilter(seoCity || searchParams.get("sehir") || "hepsi");
    const ilce = searchParams.get("ilce");
    setDistrictFilters((prev) => (ilce ? (prev.length === 1 && prev[0] === ilce ? prev : [ilce]) : (prev.length ? [] : prev)));
    window.scrollTo(0, 0);
  }, [slug, searchParams, seoCity]);

  const toggleDistrict = (d) =>
    setDistrictFilters((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(query.trim()), 300); return () => clearTimeout(t); }, [query]);

  const params = useMemo(() => ({
    category: slug,
    city: cityFilter !== "hepsi" ? cityFilter : undefined,
    district: districtFilters.length ? districtFilters.join(",") : undefined,
    q: debouncedQ || undefined,
    sort,
  }), [slug, cityFilter, districtFilters, debouncedQ, sort]);

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true);
    axios.get(`${API}/firms/search`, { params: { ...params, skip: 0, limit: PAGE } })
      .then(({ data }) => {
        if (id !== reqId.current) return;
        setVenues(data.items || []); setTotal(data.total || 0); setCityTotal(data.cityTotal || 0);
        setDistrictCounts(data.districtCounts || []); setNearby(data.nearby || []);
      })
      .catch(() => { if (id === reqId.current) { setVenues([]); setTotal(0); setNearby([]); } })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  }, [params]);

  const loadMore = () => {
    const id = reqId.current;
    setLoadingMore(true);
    axios.get(`${API}/firms/search`, { params: { ...params, skip: venues.length, limit: PAGE } })
      .then(({ data }) => { if (id === reqId.current) setVenues((prev) => [...prev, ...(data.items || [])]); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const catName = category?.name || "Kategori";
  const realCount = cityTotal;
  const seoCanonical = seoCity ? `/${slug}/${routeParams.citySlug}` : `/kategori/${slug}`;
  const seoTitle = seoCity
    ? `${seoCity} ${catName} ve Fiyatları | DüğünSenin`
    : `${catName} | DüğünSenin`;
  const seoDesc = seoCity
    ? `${seoCity} ${catName.toLowerCase()} listesi, fiyatları ve iletişim bilgileri. ${seoCity} bölgesindeki firmaları karşılaştırın, ücretsiz teklif alın.`
    : `Türkiye genelinde ${catName.toLowerCase()} listesi, fiyatları ve iletişim bilgileri. Karşılaştırın, ücretsiz teklif alın.`;
  const seoNoindex = seoCity && realCount === 0;
  const seoBreadcrumb = breadcrumbJsonLd(
    seoCity
      ? [{ name: "Ana Sayfa", url: "/" }, { name: catName, url: `/kategori/${slug}` }, { name: seoCity, url: seoCanonical }]
      : [{ name: "Ana Sayfa", url: "/" }, { name: catName, url: seoCanonical }]
  );

  return (
    <div className="bg-[#FBF5F0] min-h-screen">
      <Seo title={seoTitle} description={seoDesc} canonical={seoCanonical} ogImage={category?.image} noindex={seoNoindex} jsonLd={seoBreadcrumb} />
      <Header />

      {/* Banner */}
      <div className="relative pt-28 pb-14 overflow-hidden">
        <img
          src={category?.image}
          alt={category?.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2A2224]/85 to-[#9E2A3B]/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-5">
          <nav className="flex items-center gap-2 text-white/80 text-sm mb-3">
            <Link to="/" className="flex items-center gap-1 hover:text-white"><Home className="w-3.5 h-3.5" /> Anasayfa</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#E3CE9E]">{category?.name || "Kategori"}</span>
          </nav>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">
            {seoCity ? `${seoCity} ${category?.name || "Sonuçlar"}` : (category?.name || "Sonuçlar")}
          </h1>
          <p className="text-white/85 mt-2">{loading ? "Firmalar bulunuyor..." : `${total.toLocaleString("tr-TR")} firma / mekan listeleniyor`}</p>
        </div>
      </div>

      {/* Filters + results */}
      <div className="max-w-7xl mx-auto px-5 py-10">
        <div className="bg-white rounded-2xl border border-[#F0DDE0] p-4 md:p-5 mb-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="flex-1 flex items-center gap-2 px-3 rounded-xl bg-[#FBF5F0]">
              <Search className="w-5 h-5 text-[#C15B6B]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="İsimle ara..."
                data-testid="listing-search-input"
                className="w-full bg-transparent h-11 outline-none text-[#2A2224] placeholder:text-[#a8999b]"
              />
            </div>

            <div className="flex items-center gap-2 min-w-[180px]">
              <Select open={cityOpen} onOpenChange={setCityOpen} value={cityFilter} onValueChange={(v) => { if (seoCity) { navigate(v === "hepsi" ? `/kategori/${slug}` : `/${slug}/${citySlug(v)}`); return; } setCityFilter(v); setDistrictFilters([]); }}>
                <SelectTrigger data-testid="listing-city-select" className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                  <SlidersHorizontal className="w-4 h-4 text-[#C15B6B] mr-1" />
                  <SelectValue placeholder="Şehir" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="hepsi">Tüm Şehirler</SelectItem>
                  {CITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 min-w-[180px]">
              {cityFilter === "hepsi" ? (
                <button type="button" onClick={() => setCityOpen(true)} data-testid="listing-district-select" className="w-full flex items-center h-11 px-3 rounded-md bg-[#FBF5F0] text-sm text-[#7C6E70] hover:text-[#9E2A3B] transition-colors">
                  <MapPin className="w-4 h-4 text-[#C15B6B] mr-2" /> Önce şehir seçin
                  <ChevronDown className="w-4 h-4 ml-auto opacity-50" />
                </button>
              ) : (
              <Select
                value={districtFilters.length === 1 ? districtFilters[0] : "hepsi"}
                onValueChange={(v) => setDistrictFilters(v === "hepsi" ? [] : [v])}
              >
                <SelectTrigger data-testid="listing-district-select" className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                  <MapPin className="w-4 h-4 text-[#C15B6B] mr-1" />
                  <SelectValue placeholder="İlçe" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="hepsi">Tüm İlçeler</SelectItem>
                  {districtsOf(cityFilter).map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </div>

            <div className="min-w-[180px]">
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="listing-sort-select" className="bg-[#FBF5F0] border-0 h-11 text-[#2A2224]">
                  <SelectValue placeholder="Sırala" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="populer">Popülerlik</SelectItem>
                  <SelectItem value="puan">En Yüksek Puan</SelectItem>
                  <SelectItem value="fiyat-artan">Fiyat (Artan)</SelectItem>
                  <SelectItem value="fiyat-azalan">Fiyat (Azalan)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {cityFilter !== "hepsi" ? (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
            {/* İLÇE SIDEBAR */}
            <aside className="lg:sticky lg:top-24 h-max space-y-6">
              <div className="bg-white rounded-2xl border border-[#F0DDE0] shadow-sm overflow-hidden" data-testid="category-filter-panel">
                <button
                  onClick={() => setCatOpen((o) => !o)}
                  data-testid="category-filter-toggle"
                  className="w-full flex items-center justify-between px-5 py-4 border-b border-[#F0DDE0] hover:bg-[#FBF5F0] transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4 text-[#C15B6B]" />
                    <span className="font-display text-lg text-[#2A2224]">Kategori</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-[#9E2A3B] transition-transform ${catOpen ? "rotate-180" : ""}`} />
                </button>
                {catOpen && (
                <div className="max-h-[360px] overflow-y-auto py-1">
                  {[ALL_MEKAN, ...groupCats].map((c) => {
                    const active = c.slug === slug;
                    return (
                      <button
                        key={c.slug}
                        onClick={() => goCategory(c.slug)}
                        data-testid={`category-filter-${c.slug}`}
                        className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${active ? "bg-[#F6E4E6]" : "hover:bg-[#FBF5F0]"}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${active ? "bg-[#9E2A3B]" : "bg-[#D9C4C7]"}`} />
                        <span className={`text-sm truncate ${active ? "text-[#9E2A3B] font-medium" : "text-[#3f3335]"}`}>{c.name}</span>
                      </button>
                    );
                  })}
                </div>
                )}
              </div>

              {districtCounts.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] shadow-sm overflow-hidden" data-testid="ilce-filter-panel">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0DDE0]">
                  <button
                    onClick={() => setIlceOpen((o) => !o)}
                    data-testid="ilce-filter-toggle"
                    className="flex-1 flex items-center gap-2 text-left"
                  >
                    <MapPin className="w-4 h-4 text-[#C15B6B]" />
                    <span className="font-display text-lg text-[#2A2224]">İlçe</span>
                    <ChevronDown className={`w-4 h-4 text-[#9E2A3B] transition-transform ${ilceOpen ? "rotate-180" : ""}`} />
                  </button>
                  {districtFilters.length > 0 && (
                    <button onClick={() => setDistrictFilters([])} className="flex items-center gap-1 text-xs text-[#9E2A3B] hover:underline">
                      <X className="w-3 h-3" /> Temizle
                    </button>
                  )}
                </div>
                {ilceOpen && (
                <div className="max-h-[520px] overflow-y-auto py-1">
                  {districtCounts.map(([d, count]) => {
                    const active = districtFilters.includes(d);
                    return (
                      <button
                        key={d}
                        onClick={() => toggleDistrict(d)}
                        className={`w-full flex items-center justify-between gap-3 px-5 py-2.5 text-left transition-colors ${active ? "bg-[#F6E4E6]" : "hover:bg-[#FBF5F0]"}`}
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <Checkbox checked={active} className="pointer-events-none data-[state=checked]:bg-[#9E2A3B] data-[state=checked]:border-[#9E2A3B]" />
                          <span className={`text-sm truncate ${active ? "text-[#9E2A3B] font-medium" : "text-[#3f3335]"}`}>{d}</span>
                        </span>
                        <span className={`shrink-0 min-w-[28px] text-center px-2 py-0.5 rounded-full text-xs font-medium ${active ? "bg-[#9E2A3B] text-white" : "bg-[#F3EDEE] text-[#7C6E70]"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
              )}
            </aside>

            {/* RESULTS */}
            <div>
              {districtFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="text-sm text-[#7C6E70]">Seçili ilçeler:</span>
                  {districtFilters.map((d) => (
                    <button key={d} onClick={() => toggleDistrict(d)} className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#9E2A3B] text-white text-xs">
                      {d} <X className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              )}
              {loading && !venues.length ? <Searching /> : venues.length ? (
                <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 transition-opacity ${loading ? "opacity-40 pointer-events-none" : ""}`}>
                  {venues.map((v, i) => (
                    <VenueCard key={v.slug || v.id} venue={v} index={i % 24} />
                  ))}
                </div>
              ) : (
                <NearbyFallback city={cityFilter} districts={districtFilters} nearby={nearby} />
              )}
              {total > venues.length && <LoadMore onClick={loadMore} loading={loadingMore} left={total - venues.length} />}
            </div>
          </div>
        ) : loading && !venues.length ? <Searching /> : venues.length ? (
          <div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-opacity ${loading ? "opacity-40 pointer-events-none" : ""}`}>
            {venues.map((v, i) => (
              <VenueCard key={v.slug || v.id} venue={v} index={i % 24} />
            ))}
          </div>
          {total > venues.length && <LoadMore onClick={loadMore} loading={loadingMore} left={total - venues.length} />}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-display text-2xl text-[#2A2224]">Sonuç bulunamadı</p>
            <p className="text-[#7C6E70] mt-2">Filtreleri değiştirerek tekrar deneyin.</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CategoryListing;
